var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

import { get } from "lodash-es";
import { traverse } from "@atlaskit/adf-utils/traverse.es";
import * as ProseMirrorMenu from "prosemirror-menu";
import * as ProseMirrorState from "prosemirror-state";
import * as ProseMirrorUtils from "prosemirror-utils";
import { components, provideReactivepad, withReactivepad, createStore, formatters, getFormattedValue, SUPPORTED_FUNCTIONS, MOST_COMMON_FUNCTIONS, NODE_TYPES } from "@reactivepad/core";
import FormulaView from "./nodeViews/FormulaView";
import TableView from "./nodeViews/TableView";
import ChartView from "./nodeViews/ChartView";
import { pluginKey, TRANSACTION_METAS } from "./config";
import nodes, { NODES_NAMES, NODES_DATA_ATTRS, parsePropsFromAttrs, extractTablePropsFromAttrs } from "./nodes";

var Plugin = ProseMirrorState.Plugin;
var MenuItem = ProseMirrorMenu.MenuItem,
    Dropdown = ProseMirrorMenu.Dropdown;
var findChildren = ProseMirrorUtils.findChildren;


export { components, nodes, NODE_TYPES, NODES_NAMES, NODES_DATA_ATTRS, provideReactivepad, withReactivepad, getFormattedValue, formatters, createStore, SUPPORTED_FUNCTIONS, MOST_COMMON_FUNCTIONS };

var checkMenuItemEnabled = function checkMenuItemEnabled(nodeKey, _ref) {
  var selection = _ref.selection,
      schema = _ref.schema;
  var $from = selection.$from;

  var index = $from.index();

  return $from.parent.canReplaceWith(index, index, get(schema.nodes, nodeKey));
};

var insertNode = function insertNode(nodeKey, attrs, _ref2, dispatch) {
  var schema = _ref2.schema,
      tr = _ref2.tr;

  // ToDo: hack for problems with focus after span when span is the last node in the line
  // should be investigated and handled better
  var newTr = tr.replaceSelectionWith(schema.nodes[nodeKey].create(attrs));
  if (nodeKey === NODES_NAMES.formula) newTr = tr.insertText(" ");
  dispatch(newTr);
};

export var menuItems = [{
  key: NODES_NAMES.formula,
  title: "Insert Formula",
  label: "Formula",
  enable: function enable(state) {
    return checkMenuItemEnabled(NODES_NAMES.formula, state);
  },

  run: function run() {
    for (var _len = arguments.length, params = Array(_len), _key = 0; _key < _len; _key++) {
      params[_key] = arguments[_key];
    }

    var editorState = params[0];

    var store = pluginKey.getState(editorState);
    var formula = store.createAndAddFormula();

    insertNode.apply(undefined, [NODES_NAMES.formula, formula.serialize()].concat(params));

    setTimeout(function () {
      // ToDo: find a better solution
      formula.toggleEditing(true);
    }, 5);
  }
}, {
  key: NODES_NAMES.table,
  title: "Insert Table",
  label: "Table",
  run: function run() {
    for (var _len2 = arguments.length, params = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      params[_key2] = arguments[_key2];
    }

    var editorState = params[0];

    var store = pluginKey.getState(editorState);
    var data = store.createAndAddTable().serialize();

    insertNode.apply(undefined, [NODES_NAMES.table, data].concat(params));
  }
}, {
  key: NODES_NAMES.chart,
  title: "Insert Chart",
  label: "Chart",
  run: function run() {
    for (var _len3 = arguments.length, params = Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      params[_key3] = arguments[_key3];
    }

    var editorState = params[0];

    var store = pluginKey.getState(editorState);
    var chart = store.createAndAddChart();

    insertNode.apply(undefined, [NODES_NAMES.chart, chart.serialize()].concat(params));

    setTimeout(function () {
      // ToDo: find a better solution
      chart.toggleMenuOpen(true);
    }, 5);
  }
}];

export var buildMenuItems = function buildMenuItems(items) {
  return items.map(function (item) {
    return new MenuItem(item);
  });
};
export var buildMenuDropdown = function buildMenuDropdown(items) {
  var label = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "Reactivepad";
  return new Dropdown(buildMenuItems(items), { label: label });
};

export var syncStoreWithJson = function syncStoreWithJson(store, tr) {
  var doc = tr.doc,
      meta = tr.meta;

  // this function called by view's onSnapshot; no need to sync store

  if (get(meta, TRANSACTION_METAS.nodeSnapshot)) {
    return;
  }

  var children = findChildren(doc, function (_ref3) {
    var type = _ref3.type;

    return !!nodes[type.name];
  });

  store.nodes.forEach(function (n) {
    if (!children.some(function (_ref4) {
      var node = _ref4.node;
      return n.id === get(node, "attrs.id");
    })) {
      // ToDo: is there a better way? Solved better in Word plugin
      setTimeout(function () {
        return store.removeNode(n);
      }, 0);
    }
  });

  children.filter(function (_ref5) {
    var node = _ref5.node;
    return !store.nodes.some(function (n) {
      return n.id === get(node, "attrs.id");
    });
  }).forEach(function (_ref6) {
    var node = _ref6.node;
    var attrs = node.attrs;


    switch (attrs.type) {
      case "formula":
        {
          store.createAndAddFormula(attrs);
          break;
        }
      case "table":
        {
          store.createAndAddTable(attrs);
          break;
        }
      case "chart":
        {
          store.createAndAddChart(attrs);
          break;
        }
      default:
        break;
    }
  });
};

var buildPlugin = function buildPlugin() {
  var _nodeViews;

  var _ref7 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      storeSnapshot = _ref7.storeSnapshot,
      _ref7$readOnly = _ref7.readOnly,
      readOnly = _ref7$readOnly === undefined ? false : _ref7$readOnly,
      tableNamePrefix = _ref7.tableNamePrefix,
      _ref7$functions = _ref7.functions,
      functions = _ref7$functions === undefined ? [] : _ref7$functions,
      formulaFormRenderer = _ref7.formulaFormRenderer,
      tableRenderDelay = _ref7.tableRenderDelay,
      authProvider = _ref7.authProvider,
      container = _ref7.container;

  var store = void 0;
  var storeEnv = { tableNamePrefix: tableNamePrefix, functions: functions, authProvider: authProvider };

  return {
    plugin: new Plugin({
      key: pluginKey,
      props: {
        transformPastedHTML: function transformPastedHTML(html) {
          var wrapper = document.createElement("div");
          wrapper.innerHTML = html;

          wrapper.querySelectorAll("[" + NODES_DATA_ATTRS.type + "]").forEach(function (el) {
            var nodeType = el.getAttribute(NODES_DATA_ATTRS.type);
            var _id = void 0;
            var _attrs = void 0;
            switch (nodeType) {
              case NODE_TYPES.formula:
                {
                  var _parsePropsFromAttrs = parsePropsFromAttrs(el),
                      id = _parsePropsFromAttrs.id,
                      name = _parsePropsFromAttrs.name,
                      type = _parsePropsFromAttrs.type,
                      formatted = _parsePropsFromAttrs.formatted,
                      attrs = _objectWithoutProperties(_parsePropsFromAttrs, ["id", "name", "type", "formatted"]);

                  _id = id;
                  _attrs = attrs;
                  break;
                }
              case NODE_TYPES.table:
                {
                  var _extractTablePropsFro = extractTablePropsFromAttrs(el, true),
                      _id2 = _extractTablePropsFro.id,
                      _name = _extractTablePropsFro.name,
                      _type = _extractTablePropsFro.type,
                      _attrs2 = _objectWithoutProperties(_extractTablePropsFro, ["id", "name", "type"]);

                  _id = _id2;
                  _attrs = _attrs2;
                  break;
                }
              default:
                break;
            }

            if (store.findById(_id)) {
              var node = store.createAndAddNode(nodeType, _attrs).serialize();
              el.setAttribute(NODES_DATA_ATTRS.props, JSON.stringify(node));
            }
          });

          return wrapper.innerHTML;
        },

        nodeViews: (_nodeViews = {}, _nodeViews[NODES_NAMES.formula] = function () {
          return new (Function.prototype.bind.apply(FormulaView, [null].concat([formulaFormRenderer, container], Array.prototype.slice.call(arguments))))();
        }, _nodeViews[NODES_NAMES.table] = function () {
          return new (Function.prototype.bind.apply(TableView, [null].concat([tableRenderDelay], Array.prototype.slice.call(arguments))))();
        }, _nodeViews[NODES_NAMES.chart] = function () {
          return new (Function.prototype.bind.apply(ChartView, [null].concat(Array.prototype.slice.call(arguments))))();
        }, _nodeViews)
      },
      state: {
        init: function init(config, editorState) {
          if (!store) {
            store = createStore(_extends({}, storeSnapshot, { readOnly: readOnly }), storeEnv);
          }

          syncStoreWithJson(store, editorState.tr);
          return store;
        },
        apply: function apply(tr, store) {
          if (!tr.docChanged) return store;

          syncStoreWithJson(store, tr);

          return store;
        },
        toJSON: function toJSON(store) {
          return store.toJSON();
        },
        fromJSON: function fromJSON(config, value) {
          store = createStore(value, storeEnv);
          return store;
        }
      }
    }),
    getStore: function getStore() {
      return store;
    },
    setReadOnly: store && store.setReadOnly,
    isStoreEmpty: function isStoreEmpty() {
      return store.isEmpty;
    }
  };
};

export { buildPlugin };
export var nodeTypes = Object.keys(nodes);

export var updateNodesData = function updateNodesData(doc, nodes, _updateFn, _nodeTypes) {
  var updateFn = _updateFn || function (_node) {
    var node = nodes.find(function (_ref8) {
      var id = _ref8.id;
      return id === get(_node, "attrs.id");
    });
    if (node) {
      return _extends({}, _node, {
        attrs: _extends({}, _node.attrs, node)
      });
    }
  };
  var visitors = (_nodeTypes || nodeTypes).reduce(function (acc, next) {
    var _extends2;

    return _extends({}, acc, (_extends2 = {}, _extends2[next] = updateFn, _extends2));
  }, {});

  return traverse(doc, visitors);
};